import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LIBRARY_IMAGE } from "@/lib/content";
import SEO from "@/components/SEO";
import PracticeSignature from "@/components/PracticeSignature";
import PracticeByline from "@/components/PracticeByline";
import { breadcrumbSchema, faqPageSchema, authoredPracticePageSchema } from "@/lib/schemas";
import { Check, ArrowRight } from "lucide-react";

const PILLARS = [
  {
    no: "I",
    title: "Family Office Coordination",
    body: "Standing counsel to single-family offices — governance documents, investment entity structuring, and coordination with in-house CFOs and external fiduciaries. We serve as the legal seat at the family table.",
  },
  {
    no: "II",
    title: "Multi-Generational Wealth",
    body: "Dynasty trusts, GST-exempt planning, and generation-skipping structures designed to endure across three generations without reformation. Texas 300-year perpetuities utilized where advantageous.",
  },
  {
    no: "III",
    title: "Concierge Legal Counsel",
    body: "Direct-line access for matters personal, familial, and enterprise — from a discreet real-estate closing to a delicate family conversation. Called first, briefed once, resolved quietly.",
  },
  {
    no: "IV",
    title: "Fiduciary Oversight",
    body: "Independent review of trustees, executors, and investment advisors. We audit fiduciary conduct, verify accountings, coordinate with beneficiaries — and, when necessary, remove and replace.",
  },
  {
    no: "V",
    title: "Legacy Governance",
    body: "Family constitutions, heir councils, and succession charters that transmit not merely assets, but the judgment, values, and standards of stewardship that produced them.",
  },
  {
    no: "VI",
    title: "Cross-Border Coordination",
    body: "For families holding interests in India, the U.K., or the E.U. — coordinated planning across jurisdictions, with local counsel of record engaged as needed. Pre-immigration tax structuring and EB-5 investor coordination.",
  },
];

const PROCESS = [
  { no: "01", title: "Referral or Introduction", body: "New relationships arrive through a trusted advisor — a CPA, wealth manager, private banker, or existing client. Cold intake is rare and by exception." },
  { no: "02", title: "Preliminary Conference", body: "A private conversation — often over an unhurried lunch or at the family's residence — to determine whether the fit is mutual. Complimentary. Under no obligation." },
  { no: "03", title: "Engagement Letter", body: "If both parties elect to proceed, a bespoke annual retainer engagement letter is executed. Scope, standard of communication, and terms are set explicitly." },
  { no: "04", title: "Standing Counsel", body: "Direct-line access. Monthly touch-points at minimum. Coordinated meetings with your CPA, wealth advisor, and trustee at least quarterly. Documented decisions, filed and retrievable." },
];

const PRIVATE_CLIENT_FAQ = [
  {
    q: "Who qualifies for Private Client engagement at Lone Star Counsel?",
    a: "Private Client engagement is offered to Texas families and family offices with material multi-generational holdings, business owners in a mature enterprise stage, and high-net-worth individuals whose affairs benefit from standing legal counsel rather than transactional representation. Engagement is typically by referral from a trusted advisor.",
  },
  {
    q: "How does Private Client engagement differ from standard legal representation?",
    a: "Standard representation is matter-based — engaged for a discrete transaction and concluded upon delivery. Private Client engagement is relational and continuous — an annual retainer that entitles the family to direct-line access, standing coordination with the client's CPA, wealth advisor and trustee, quarterly review meetings, and priority handling on any legal matter arising during the retainer year.",
  },
  {
    q: "What is the annual retainer for Private Client engagement?",
    a: "Retainer levels are set individually based on scope, family complexity, and coordination requirements. Terms are furnished on request following a preliminary conference. New Private Client relationships are limited each year to preserve the standard of service.",
  },
  {
    q: "Does Private Client engagement replace the need for a CPA or wealth advisor?",
    a: "No. Private Client counsel coordinates with — and never displaces — the family's existing CPA, wealth advisor, trustee, and family office CFO. Darryl V. Pratt is dually licensed as attorney and CPA, which allows the firm to speak both languages fluently and act as the bridge between legal, tax, and financial advisory functions.",
  },
  {
    q: "How is confidentiality maintained?",
    a: "Private Client matters are handled personally by Darryl V. Pratt. Communications remain within a small, credentialed circle. Names, transactions, and family circumstances are never publicized. Discretion is a covenant of the engagement.",
  },
];

const ROLES = [
  { key: "principal", label: "Family Principal" },
  { key: "advisor", label: "Trusted Advisor (CPA / Wealth Advisor / Banker)" },
  { key: "family_office_cfo", label: "Family Office CFO or Director" },
  { key: "trustee", label: "Trustee or Fiduciary" },
  { key: "other", label: "Other" },
];

const RETAINER_RANGES = [
  "Exploratory · Not yet determined",
  "$25,000 – $50,000 annually",
  "$50,000 – $100,000 annually",
  "$100,000 – $250,000 annually",
  "$250,000+ annually",
  "Prefer to discuss privately",
];

export default function PrivateClient() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    role: "",
    referral_source: "",
    estimated_value: "",
    location: "",
    message: "",
    preferred_contact: "email",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit =
    form.full_name.trim() &&
    form.email.trim() &&
    form.role &&
    form.message.trim().length >= 15;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/intake", {
        ...form,
        practice_area: "Private Client Services",
        stage: "legacy",
        matter_class: "private_client",
      });
      setDone(true);
      toast.success("Your private inquiry has been received.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-ivory" data-testid="private-client-page">
      <SEO
        title="Private Client Services — Family Office & HNW Counsel in Texas"
        description="Standing legal counsel for Texas families, family offices, and high-net-worth principals. Dynasty trusts, fiduciary oversight, concierge legal, cross-border coordination. By referral."
        path="/practice/private-client"
        schema={[
          ...authoredPracticePageSchema({
            path: "/practice/private-client",
            title: "Private Client Services — Family Office & HNW Counsel in Texas",
            description: "Standing legal counsel for Texas families, family offices, and high-net-worth principals. Dynasty trusts, fiduciary oversight, concierge legal, cross-border coordination. By referral.",
            discipline: "Private Client",
            minutes: 9,
            lastReviewed: "2026-02-01",
            publishedDate: "2024-06-01",
          }),
          faqPageSchema(PRIVATE_CLIENT_FAQ),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/practice" },
            { name: "Private Client Services", path: "/practice/private-client" },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="relative panel-navy min-h-[75vh] flex items-end" data-testid="pc-hero">
        <div className="absolute inset-0">
          <img src={LIBRARY_IMAGE} alt="Private library" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/50 to-navy/95" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-32 md:py-40 w-full">
          <div className="kicker kicker-ivory">Private Client Services</div>
          <div className="mt-3 text-[11px] uppercase tracking-[0.42em] text-brass font-medium">
            By Referral <span className="text-brass/50 mx-2">·</span> Annual Retainer <span className="text-brass/50 mx-2">·</span> Standing Counsel
          </div>
          <h1 className="mt-8 font-display text-ivory text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl">
            For the <br/>
            <span className="italic font-cormorant text-brass">quietly consequential.</span>
          </h1>
          <p className="mt-8 max-w-2xl font-cormorant italic text-2xl md:text-3xl text-ivory/85 leading-snug">
            A private tier of engagement for Texas families whose affairs require singular
            discretion, coordination, and continuity.
          </p>
        </div>
      </section>

      <PracticeByline
        minutes={9}
        lastReviewed="February 2026"
        discipline="Private Client"
      />

      <PracticeSignature
        src="/img/practice-private-client.jpg"
        kicker="A Signature Detail"
        caption="The heirloom watch, the family bible, the wax seal — the quiet instruments of a name held across generations."
      />

      {/* PHILOSOPHY */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="kicker">The Standing Retainer</div>
            <h2 className="mt-6 font-display text-4xl md:text-6xl leading-[1.02]">
              Not a matter.<br/>
              <span className="italic font-cormorant text-oxblood">A relationship.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-lg md:text-xl font-light text-slate2 leading-relaxed space-y-6">
            <p className="dropcap">
              Most legal engagements are transactional — a document drafted, a matter closed, a
              file archived. Private Client engagement is different. Retained on annual terms,
              our Private Client relationships extend beyond discrete matters into standing
              counsel: the legal seat at the family table.
            </p>
            <p>
              We coordinate with your CPA, wealth advisor, trustee, and family office CFO to
              ensure every decision is deliberate, documented, and durable. We are called
              first — before the transaction, before the conflict, before the tax year closes —
              and briefed once, so the family&apos;s judgment is preserved across generations of
              advisors.
            </p>
            <div className="pt-4">
              <a href="#inquire" className="btn-primary" data-testid="pc-inquire-cta">
                Inquire Privately →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SIX PILLARS */}
      <section className="bg-linen py-24 md:py-32" data-testid="pc-pillars">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="kicker">The Six Pillars</div>
            <h2 className="mt-6 font-display text-5xl md:text-6xl leading-[0.98]">
              What a Private Client<br/>
              <span className="italic font-cormorant text-oxblood">engagement includes.</span>
            </h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
            {PILLARS.map((p, i) => (
              <article key={p.no} className="hairline-top pt-6" data-testid={`pc-pillar-${i}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-cormorant italic text-3xl text-brass">{p.no}</span>
                  <h3 className="font-display text-2xl md:text-[1.65rem] text-navy leading-[1.15]">
                    {p.title}
                  </h3>
                </div>
                <p className="mt-4 text-base text-slate2 font-light leading-relaxed">
                  {p.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="pc-process">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="kicker">How We Begin</div>
            <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
              Four steps,<br/>
              <span className="italic font-cormorant text-oxblood">measured in weeks.</span>
            </h2>
            <p className="mt-8 text-slate2 font-light leading-relaxed">
              New Private Client relationships are limited each year — typically three to five —
              to preserve the standard of service that defines the engagement.
            </p>
          </div>
          <ol className="lg:col-span-7 lg:col-start-6 space-y-10">
            {PROCESS.map((s, i) => (
              <li key={s.no} className="grid grid-cols-12 gap-6 items-baseline hairline-top pt-8" data-testid={`pc-step-${i}`}>
                <div className="col-span-2 font-cormorant italic text-4xl md:text-5xl text-brass">{s.no}</div>
                <div className="col-span-10">
                  <h3 className="font-display text-2xl md:text-3xl text-navy leading-[1.1]">{s.title}</h3>
                  <p className="mt-3 text-base md:text-lg font-light text-slate2 leading-relaxed">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* RETAINER & TERMS */}
      <section className="panel-navy py-24 md:py-32" data-testid="pc-terms">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="kicker kicker-ivory">Retainer & Terms</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl text-ivory leading-[1.02]">
                Terms furnished<br/>
                <span className="italic font-cormorant text-brass">on request.</span>
              </h2>
            </div>
            <ul className="lg:col-span-6 lg:col-start-7 space-y-6 text-ivory/85 font-light">
              {[
                "Annual retainer engagement. Scope, coordination cadence, and communication standards set explicitly in the engagement letter.",
                "New Private Client relationships accepted by referral from an existing client, CPA, wealth advisor, or trusted intermediary. Cold engagement is rare and by exception.",
                "All Private Client work handled personally by Darryl V. Pratt. Of Counsel affiliates engaged only for matters requiring particular specialty (out-of-state probate, cross-border tax, niche litigation).",
                "Retainer terms are individually structured — informed by scope, coordination complexity, and the depth of standing service required.",
                "Discretion is a covenant of the engagement. Names, holdings, transactions, and family circumstances are not publicized and are shared only within a small credentialed circle.",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-4 hairline-top pt-5" data-testid={`pc-term-${i}`}>
                  <Check size={18} className="text-brass shrink-0 mt-1" />
                  <span className="text-base md:text-lg leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section id="inquire" className="bg-ivory py-24 md:py-32" data-testid="pc-inquiry">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="kicker">Inquire Privately</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
                Begin a<br/>
                <span className="italic font-cormorant text-oxblood">private conversation.</span>
              </h2>
              <p className="mt-8 text-slate2 font-light leading-relaxed">
                All inquiries are reviewed personally by Darryl V. Pratt. A response is furnished
                within two business days. This inquiry is privileged from the first keystroke.
              </p>
            </div>

            {done ? (
              <div className="lg:col-span-7 lg:col-start-6" data-testid="pc-inquiry-success">
                <div className="kicker">Received</div>
                <h3 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02]">
                  Your inquiry has been<br/>
                  <span className="italic font-cormorant text-oxblood">received.</span>
                </h3>
                <p className="mt-8 text-lg text-slate2 font-light leading-relaxed">
                  Darryl V. Pratt will review your particulars personally and respond within
                  two business days. If you were referred by an existing advisor or client, they
                  will be notified of the introduction as a courtesy.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to="/" className="btn-ghost" data-testid="pc-return-home">Return Home</Link>
                  <Link to="/practice" className="btn-ghost" data-testid="pc-view-services">View Full Services</Link>
                </div>
              </div>
            ) : (
              <form className="lg:col-span-7 lg:col-start-6 space-y-8" onSubmit={submit} data-testid="pc-inquiry-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field label="Full Name" required>
                    <input className="field" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="pc-full-name" />
                  </Field>
                  <Field label="Email Address" required>
                    <input className="field" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="pc-email" />
                  </Field>
                  <Field label="Telephone">
                    <input className="field" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="pc-phone" />
                  </Field>
                  <Field label="Location (City, State)">
                    <input className="field" value={form.location} onChange={(e) => update("location", e.target.value)} data-testid="pc-location" placeholder="Dallas, Texas" />
                  </Field>
                </div>

                <Field label="Your Role" required>
                  <div className="flex flex-wrap gap-2" data-testid="pc-role-group">
                    {ROLES.map((r) => (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => update("role", r.key)}
                        data-testid={`pc-role-${r.key}`}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition ${
                          form.role === r.key
                            ? "bg-oxblood text-ivory border-oxblood"
                            : "bg-ivory border-transparent hover:border-oxblood"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field label="Referring Advisor or Existing Client (if any)">
                    <input
                      className="field"
                      value={form.referral_source}
                      onChange={(e) => update("referral_source", e.target.value)}
                      data-testid="pc-referral"
                      placeholder="Name of CPA, wealth advisor, or referrer"
                    />
                  </Field>
                  <Field label="Anticipated Retainer Range">
                    <select
                      className="field"
                      value={form.estimated_value}
                      onChange={(e) => update("estimated_value", e.target.value)}
                      data-testid="pc-retainer-range"
                    >
                      <option value="">Select a range…</option>
                      {RETAINER_RANGES.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field
                  label="In your own words — what would you like to discuss?"
                  required
                >
                  <textarea
                    className="field"
                    rows={7}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    data-testid="pc-message"
                    placeholder="Family composition, holdings, current advisor arrangement, and what has brought you to seek Private Client counsel now."
                  />
                </Field>

                <Field label="Preferred Contact">
                  <select className="field" value={form.preferred_contact} onChange={(e) => update("preferred_contact", e.target.value)} data-testid="pc-preferred-contact">
                    <option value="email">By Email</option>
                    <option value="phone">By Telephone</option>
                    <option value="post">By Post</option>
                  </select>
                </Field>

                <div className="hairline pt-8 flex flex-wrap items-center justify-between gap-6">
                  <p className="text-xs text-slate2/70 font-cormorant italic max-w-md leading-snug">
                    Submitting this form does not create an attorney–client relationship. It is
                    a private inquiry, reviewed personally, and treated in strict confidence.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting || !canSubmit}
                    className="btn-brass disabled:opacity-40 inline-flex items-center gap-2"
                    data-testid="pc-submit"
                  >
                    {submitting ? "Submitting…" : (<>Submit Privately <ArrowRight size={16} /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="field-label mb-2">
        {label} {required && <span className="text-oxblood">·</span>}
      </div>
      {children}
    </label>
  );
}
